import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchEverything } from "../../api/customers";
import { formatMoney } from "../../utils/money";
import { formatEasternDate } from "../../utils/dates";
import { stageOf, STAGE_META } from "../../utils/jobs";

// Find anyone or any job from anywhere: name, email, phone, company, quote
// number, event title or location.

export default function SearchOverlay({ onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults(null);
      return undefined;
    }
    let active = true;
    setBusy(true);
    const t = setTimeout(() => {
      searchEverything(term)
        .then((r) => active && setResults(r))
        .catch(() => active && setResults({ customers: [], jobs: [], leads: [] }))
        .finally(() => active && setBusy(false));
    }, 200);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [q]);

  const go = (path) => {
    onClose();
    navigate(path);
  };
  const empty = results && !results.customers.length && !results.jobs.length && !results.leads.length;

  return (
    <div className="fixed inset-0 z-50 bg-birdie-charcoal/40" onClick={onClose}>
      <div
        className="mx-auto max-w-xl bg-white sm:mt-16 sm:rounded-2xl shadow-xl min-h-full sm:min-h-0 sm:max-h-[75vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-birdie-green/15 px-4 py-3">
          <SearchIcon className="w-5 h-5 text-birdie-charcoal/40 shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, phone, email, quote #…"
            className="flex-1 min-w-0 py-1.5 text-base outline-none bg-transparent"
            aria-label="Search"
            enterKeyHint="search"
          />
          <button type="button" onClick={onClose} className="text-sm font-semibold text-birdie-charcoal/60 px-2">
            Close
          </button>
        </div>

        <div className="overflow-y-auto px-2 py-2">
          {!results ? (
            <p className="px-3 py-6 text-sm text-birdie-charcoal/50 text-center">
              {busy ? "Searching…" : "Type at least 2 letters or digits."}
            </p>
          ) : empty ? (
            <p className="px-3 py-6 text-sm text-birdie-charcoal/50 text-center">Nothing matches “{q.trim()}”.</p>
          ) : (
            <>
              <Group title="Customers" items={results.customers}>
                {(c) => (
                  <Result
                    key={`c-${c.id}`}
                    onClick={() => go(`/admin/customers/${c.id}`)}
                    title={c.full_name}
                    sub={[c.company_name, c.phone, c.email].filter(Boolean).join(" · ")}
                    meta={`${c.jobs} job${Number(c.jobs) === 1 ? "" : "s"}`}
                  />
                )}
              </Group>
              <Group title="Jobs" items={results.jobs}>
                {(j) => {
                  const meta = STAGE_META[stageOf(j)];
                  return (
                    <Result
                      key={`j-${j.id}`}
                      onClick={() => go(`/admin/customers/${j.customer_id}?job=${j.id}`)}
                      title={`${j.customer_name} · ${j.quote_number}`}
                      sub={[j.title, j.event_date && formatEasternDate(j.event_date)].filter(Boolean).join(" · ")}
                      meta={
                        <>
                          {meta && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>}
                          <span className="block mt-0.5">{formatMoney(j.total_cents)}</span>
                        </>
                      }
                    />
                  );
                }}
              </Group>
              <Group title="Leads" items={results.leads}>
                {(l) => (
                  <Result
                    key={`l-${l.id}`}
                    onClick={() => go(`/admin?lead=${l.id}`)}
                    title={l.name || l.email || l.phone}
                    sub={[l.event_type, l.event_date_text, l.phone].filter(Boolean).join(" · ")}
                    meta="Lead"
                  />
                )}
              </Group>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Group({ title, items, children }) {
  if (!items?.length) return null;
  return (
    <div className="mb-2">
      <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-birdie-charcoal/45">{title}</div>
      {items.map(children)}
    </div>
  );
}

function Result({ title, sub, meta, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between gap-3 text-left px-3 py-2.5 rounded-xl hover:bg-birdie-cream"
    >
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-birdie-charcoal truncate">{title}</span>
        {sub && <span className="block text-xs text-birdie-charcoal/55 truncate">{sub}</span>}
      </span>
      <span className="shrink-0 text-right text-[11px] text-birdie-charcoal/50">{meta}</span>
    </button>
  );
}

export function SearchIcon({ className = "w-5 h-5" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path strokeLinecap="round" d="m20 20-3.5-3.5" />
    </svg>
  );
}
